import React from 'react';
import Box from '@amzn/meridian/box';
import { AssignmentAssociate, ProcessPath } from '../../assignments/models';
import { compareByDisplayName, deriveStationLabel } from '../utils';
import AssociateRow from './AssociateRow';

export interface LaborBoardProps {
  associates: AssignmentAssociate[];
  processPaths: ProcessPath[];
}

/**
 * The Labor board grid on the Associate View Page.
 *
 * Sorts the supplied associates alphabetically (case-insensitive) by their
 * compact display name (Req 4.3), derives the station label for each once
 * by walking `processPaths` (Req 6.1, 6.2, 6.3), and renders exactly one
 * `AssociateRow` per associate (Req 4.1).
 *
 * The rendered DOM is a flat list inside a single grid container
 * (`<div className="labor-board__grid">`). The responsive 2/3/4-column
 * layout is handled entirely by CSS Grid media queries defined in the
 * page-level SCSS (see task 9.2), so this component does not measure the
 * viewport and does not need to know the column count. CSS Grid's default
 * `grid-auto-flow: row` placement flows the flat sorted list left-to-right
 * then top-to-bottom at whatever column count the viewport resolves to.
 *
 * Meridian `Box` is used for the outer wrapper so the component inherits
 * the system's base layout primitive; the grid element itself is a plain
 * styled `<div>` so `display: grid` applies predictably (Meridian `Box`
 * wraps its children, which can interfere with grid flow).
 *
 * Keyed on `associateId` (assumed unique within a single response per the
 * assignments-tab invariants) so React can reconcile efficiently if the
 * list re-sorts.
 */
const LaborBoard: React.FC<LaborBoardProps> = ({ associates, processPaths }) => {
  const sortedAssociates = [...associates].sort(compareByDisplayName);

  return (
    <Box className="labor-board">
      <div className="labor-board__grid" data-testid="labor-board-grid">
        {sortedAssociates.map(associate => (
          <AssociateRow
            key={associate.associateId}
            associate={associate}
            stationLabel={deriveStationLabel(associate, processPaths)}
          />
        ))}
      </div>
    </Box>
  );
};

export default LaborBoard;
